import type React from "react";
import {
  Banana,
  BarChart3,
  Brain,
  Facebook,
  Film,
  Palette,
  Search,
  Video,
  type LucideIcon,
} from "lucide-react";
import { cdnUrl } from "@/lib/utils";
import { AlienAvatar } from "@/features/ads/components/AlienAvatar";
import { GoogleAdsAvatar } from "@/features/ads/components/GoogleAdsAvatar";
import { AgentScoutAvatar } from "@/features/ads/components/AgentScoutAvatar";
import { AgentBananAvatar } from "@/features/ads/components/AgentBananAvatar";
import { AgentVidAvatar } from "@/features/ads/components/AgentVidAvatar";
import type { SubAgentId } from "@/features/ads";

export type CompanionAgentId = "marcus" | SubAgentId;
export type CompanionSubAgentRouting = "chat" | "scout-research" | "moodboard-workflow";
export type CompanionMediaPreviewBehavior = "auto-generated-media" | "none";
export type CompanionRuntimeTarget = "anthropic_sdk" | "unified";
export type AssetStripSubAgentId =
  | "banan"
  | "vid"
  | "higgsfield"
  | "higgsfield_cli"
  | "agent_v";

type ChatIcon = React.ComponentType<{
  className?: string;
  style?: React.CSSProperties;
}>;

/**
 * Avatar component that may opt into a true-3D (R3F) render via `render3d`.
 * Only single-instance surfaces (chat header) should pass it — list rows keep
 * the CSS fallback to avoid exhausting WebGL contexts.
 */
type AgentAvatar = React.ComponentType<{
  className?: string;
  style?: React.CSSProperties;
  render3d?: boolean;
}>;

export interface CompanionMarcusAgent {
  id: "marcus";
  name: string;
  role: string;
  tag: string;
  icon: LucideIcon;
  color: string;
  bgHex: string;
  imageSrc: string;
  supportsVoiceInput: boolean;
  supportsSessions: boolean;
  supportsAssetStrip: boolean;
}

export interface CompanionSubAgent {
  id: string;
  /** Maps to SubAgentId in the ads store for chat/session state. */
  subAgentId: SubAgentId;
  /** Logical agent name sent to the backend managed-agent endpoint. */
  backendAgentId: string;
  /** Runtime endpoint family used by the shared chat hook. */
  runtimeTarget?: CompanionRuntimeTarget;
  /** DB-authored unified-runtime agent_name when it differs from backendAgentId. */
  agentName?: string;
  /** Compact-list/header display label. */
  name: string;
  role: string;
  tag: string;
  color: string;
  bgHex: string;
  icon: LucideIcon;
  /**
   * Optional full-bleed avatar used in the chat-list row when present.
   * Supersedes `icon` for that surface only — other consumers (sub-agent
   * activity badge, panel header fallback) keep using the LucideIcon.
   */
  listAvatar?: AgentAvatar;
  /** Direct-chat display label, where it intentionally differs from list chrome. */
  chatName: string;
  chatRole: string;
  chatBgHex: string;
  chatIcon: ChatIcon;
  greeting: string;
  mediaPreviewBehavior: CompanionMediaPreviewBehavior;
  supportsVoiceInput: boolean;
  supportsSessions: boolean;
  supportsAssetStrip: boolean;
  routing: CompanionSubAgentRouting;
  showInChatList: boolean;
  /** Show this row even before Marcus or a persisted summary reveals it. */
  alwaysVisible?: boolean;
}

export type CompanionListAgent = CompanionMarcusAgent | CompanionSubAgent;
export type ChatRenderableSubAgent = CompanionSubAgent & { routing: "chat" };

function AgentMadsLogo({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  const classes = ["rounded-full object-cover", className].filter(Boolean).join(" ");
  return <img src={cdnUrl("/agent-mads-logo.png")} alt="Meta Ads" className={classes} style={style} />;
}

export const MARCUS_CHAT_AGENT: CompanionMarcusAgent = {
  id: "marcus",
  name: "Marcus",
  role: "Ads Orchestrator Agent",
  tag: "Growth",
  icon: Brain,
  color: "text-primary",
  bgHex: "#8b5cf6",
  imageSrc: cdnUrl("/Marcus-onboarding.jpg"),
  supportsVoiceInput: true,
  supportsSessions: true,
  supportsAssetStrip: false,
};

export const COMPANION_SUB_AGENT_REGISTRY = {
  banan: {
    id: "img-agent",
    subAgentId: "banan",
    backendAgentId: "banan",
    name: "Agent Banan",
    role: "Image Gen",
    tag: "Images",
    color: "text-yellow-500",
    bgHex: "#eab308",
    icon: Banana,
    listAvatar: AgentBananAvatar,
    chatName: "Agent Banan",
    chatRole: "Image Generation",
    chatBgHex: "#eab308",
    chatIcon: Banana,
    greeting: "Hey! I'm Agent Banan — I generate ad images. Tell me what you need!",
    mediaPreviewBehavior: "auto-generated-media",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: true,
    routing: "chat",
    showInChatList: true,
  },
  vid: {
    id: "vid-agent",
    subAgentId: "vid",
    backendAgentId: "vid",
    name: "Agent Vid",
    role: "Video Gen",
    tag: "Videos",
    color: "text-blue-500",
    bgHex: "#3b82f6",
    icon: Video,
    listAvatar: AgentVidAvatar,
    chatName: "Agent Vid",
    chatRole: "Video Generation",
    chatBgHex: "#3b82f6",
    chatIcon: Video,
    greeting: "Hi! I'm Agent Vid — I create ad videos. What would you like to make?",
    mediaPreviewBehavior: "auto-generated-media",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: true,
    routing: "chat",
    showInChatList: true,
  },
  agent_v: {
    id: "agent-v",
    subAgentId: "agent_v",
    backendAgentId: "agent_v",
    runtimeTarget: "unified",
    agentName: "agent_v",
    name: "Agent V",
    role: "Video Agent",
    tag: "Videos",
    color: "text-teal-500",
    bgHex: "#14b8a6",
    icon: Film,
    chatName: "Agent V",
    chatRole: "Video Agent",
    chatBgHex: "#14b8a6",
    chatIcon: Film,
    greeting: "Tell me what video to create or revise.",
    mediaPreviewBehavior: "auto-generated-media",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: true,
    routing: "chat",
    showInChatList: true,
    alwaysVisible: true,
  },
  scout: {
    id: "res-agent",
    subAgentId: "scout",
    backendAgentId: "scout",
    name: "Agent Scout",
    role: "Research",
    tag: "Research",
    color: "text-purple-500",
    bgHex: "#a855f7",
    icon: Search,
    listAvatar: AgentScoutAvatar,
    chatName: "Agent Scout",
    chatRole: "Research",
    chatBgHex: "#a855f7",
    chatIcon: Search,
    greeting: "Agent Scout is gathering research for this workspace.",
    mediaPreviewBehavior: "none",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: false,
    routing: "scout-research",
    showInChatList: true,
  },
  gads: {
    id: "gads-agent",
    subAgentId: "gads",
    backendAgentId: "gads",
    name: "Agent G",
    role: "Google Ads",
    tag: "Google Ads",
    color: "text-sky-500",
    bgHex: "#0ea5e9",
    icon: BarChart3,
    listAvatar: GoogleAdsAvatar,
    chatName: "Agent G",
    chatRole: "Google Ads",
    chatBgHex: "#4285F4",
    chatIcon: BarChart3,
    greeting: "Hi! I'm Agent G — tell me what campaign to build.",
    mediaPreviewBehavior: "none",
    supportsVoiceInput: true,
    supportsSessions: true,
    supportsAssetStrip: false,
    routing: "chat",
    showInChatList: true,
  },
  meta_ads: {
    id: "meta-ads-agent",
    subAgentId: "meta_ads",
    backendAgentId: "meta_ads",
    name: "Meta Ads",
    role: "Facebook & Instagram",
    tag: "Meta Ads",
    color: "text-blue-600",
    bgHex: "#1877F2",
    icon: Facebook,
    listAvatar: AlienAvatar,
    chatName: "Meta Ads",
    chatRole: "Meta Ads",
    chatBgHex: "#1877F2",
    chatIcon: AgentMadsLogo,
    greeting:
      "Hi! I'm Meta Ads — I run real Meta (Facebook + Instagram) campaigns end-to-end: uploads, campaigns, ad sets, creatives, insights, the lot. Tell me what to do and I'll get going.",
    mediaPreviewBehavior: "none",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: false,
    routing: "chat",
    showInChatList: true,
  },
  higgsfield: {
    id: "higgsfield-agent",
    subAgentId: "higgsfield",
    backendAgentId: "higgsfield",
    name: "Agent Vidzz",
    role: "Marketing Studio",
    tag: "Marketing Studio",
    color: "text-purple-500",
    bgHex: "#a855f7",
    icon: Film,
    chatName: "Agent Vidzz",
    chatRole: "Marketing Studio",
    chatBgHex: "#a855f7",
    chatIcon: Film,
    greeting: "Hey! I'm Agent Vidzz — give me a brief and I'll generate the videos.",
    mediaPreviewBehavior: "auto-generated-media",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: true,
    routing: "chat",
    showInChatList: true,
  },
  higgsfield_cli: {
    id: "higgsfield-cli-agent",
    subAgentId: "higgsfield_cli",
    backendAgentId: "higgsfield_cli",
    name: "Agent Higgs",
    role: "Ecom Video Maker",
    tag: "Ecom Videos",
    color: "text-orange-500",
    bgHex: "#f97316",
    icon: Film,
    chatName: "Agent Higgs",
    chatRole: "Ecom Video Maker",
    chatBgHex: "#f97316",
    chatIcon: Film,
    greeting:
      "Hi! I'm Agent Higgs — give me product images or a brief and I'll spin up ad videos, character portraits, or product photoshoots end-to-end.",
    mediaPreviewBehavior: "auto-generated-media",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: true,
    routing: "chat",
    showInChatList: true,
  },
  moodboard: {
    id: "moodboard-agent",
    subAgentId: "moodboard",
    backendAgentId: "moodboard",
    name: "Agent Moodboard",
    role: "Moodboard",
    tag: "Moodboard",
    color: "text-fuchsia-500",
    bgHex: "#d946ef",
    icon: Palette,
    chatName: "Agent Moodboard",
    chatRole: "Moodboard",
    chatBgHex: "#d946ef",
    chatIcon: Palette,
    greeting: "Agent Moodboard is preparing creative references for this workspace.",
    mediaPreviewBehavior: "none",
    supportsVoiceInput: false,
    supportsSessions: true,
    supportsAssetStrip: false,
    routing: "moodboard-workflow",
    showInChatList: false,
  },
} satisfies Record<SubAgentId, CompanionSubAgent>;

export const COMPANION_SUB_AGENT_ORDER: SubAgentId[] = [
  "banan",
  "vid",
  "agent_v",
  "scout",
  "gads",
  "meta_ads",
  "higgsfield",
  "higgsfield_cli",
  "moodboard",
];

export const ADS_SUB_AGENTS: CompanionSubAgent[] = COMPANION_SUB_AGENT_ORDER.map(
  (id) => COMPANION_SUB_AGENT_REGISTRY[id],
).filter((agent) => agent.showInChatList);

export const SESSION_CAPABLE_SUB_AGENT_IDS: SubAgentId[] = COMPANION_SUB_AGENT_ORDER.filter(
  (id) => COMPANION_SUB_AGENT_REGISTRY[id].supportsSessions,
);

export const COMPANION_AGENT_TAGS: Record<CompanionAgentId, string> = {
  marcus: MARCUS_CHAT_AGENT.tag,
  banan: COMPANION_SUB_AGENT_REGISTRY.banan.tag,
  vid: COMPANION_SUB_AGENT_REGISTRY.vid.tag,
  agent_v: COMPANION_SUB_AGENT_REGISTRY.agent_v.tag,
  scout: COMPANION_SUB_AGENT_REGISTRY.scout.tag,
  gads: COMPANION_SUB_AGENT_REGISTRY.gads.tag,
  meta_ads: COMPANION_SUB_AGENT_REGISTRY.meta_ads.tag,
  higgsfield: COMPANION_SUB_AGENT_REGISTRY.higgsfield.tag,
  higgsfield_cli: COMPANION_SUB_AGENT_REGISTRY.higgsfield_cli.tag,
  moodboard: COMPANION_SUB_AGENT_REGISTRY.moodboard.tag,
};

export function getCompanionSubAgent(agentId: SubAgentId): CompanionSubAgent {
  return COMPANION_SUB_AGENT_REGISTRY[agentId];
}

export function getChatRenderableSubAgent(
  agentId: SubAgentId,
): ChatRenderableSubAgent | null {
  const definition = COMPANION_SUB_AGENT_REGISTRY[agentId];
  return definition.routing === "chat" ? (definition as ChatRenderableSubAgent) : null;
}

export function supportsSubAgentAssetStrip(
  agentId: SubAgentId,
): agentId is AssetStripSubAgentId {
  return COMPANION_SUB_AGENT_REGISTRY[agentId].supportsAssetStrip;
}
